import { useMemo } from 'react';

import {
  AdminImpersonationApiFactory,
  ApplicationDataUpdateApiFactory,
  AppStatsReaderRestApiFactory,
  CertificationManagementApiFactory,
  CertificationPlanStageApiFactory,
  CertificationStageApiFactory,
  CommissionCandidatesApiFactory,
  Configuration,
  DevelopmentCenterParticipantApiFactory,
  DevelopmentCenterStageApiFactory,
  DevelopmentCycleCampaignsApiFactory,
  DevPlanEvalApiFactory,
  DictionariesApiFactory,
  EmployeeCertificationApiFactory,
  EmployeeDevelopmentPathApiFactory,
  EmployeeProfileAvatarReaderRestApiFactory,
  EmployeeProfileAvatarUpdaterRestApiFactory,
  EmployeeProfileSearchApiFactory,
  EmployeeSearchApiFactory,
  EmployeeTalentPanelApiFactory,
  ExamDayApiFactory,
  ExamDayParticipantApiFactory,
  ExamineeExamApiFactory,
  ExamineeExamPreviewApiFactory,
  ExamParticipantApiFactory,
  ExamStageApiFactory,
  ExtraCommissionMembersApiFactory,
  FilesTemplatesRestApiFactory,
  HierarchySimulationRestApiFactory,
  HierarchyUnitsManagerRestApiFactory,
  HierarchyUnitsReaderRestApiFactory,
  HrStatisticsApiFactory,
  IndividualExamApiFactory,
  LegalDocumentsApiFactory,
  LegalDocumentsManagementApiFactory,
  NewsApiFactory,
  NewsManagementApiFactory,
  PracticalExamActivityReportApiFactory,
  PracticalExamAttemptApiFactory,
  PracticalExamQuestionPoolApiFactory,
  PracticalExamReportAttachmentPreviewApiFactory,
  PracticalExamSecurityEventApiFactory,
  PracticalExamShareTokenApiFactory,
  PracticalExamStatisticsApiFactory,
  RestCurrentUserApiFactory,
  RestEmployeeProfileApiFactory,
  RestEmployeeSectionsReaderApiFactory,
  RestEmployeeSectionsUpdaterApiFactory,
  RestFeatureFlagsApiFactory,
  RestNotificationControllerApiFactory,
  RestUserPermissionsApiFactory,
  RestUserRolesApiFactory,
  RestUsersManagementApiFactory,
  StageParticipantApiFactory,
  StructureBrowserApiFactory,
  SuccessionCareerPathRestApiFactory,
  SuccessionChangeLogRestApiFactory,
  SuccessionReportRestApiFactory,
  SuccessionSearchRestApiFactory,
  SuccessorPositionStateRestApiFactory,
  SurveyApiFactory,
  SurveyManagementApiFactory,
  SurveyParticipationApiFactory,
  TasksApiFactory,
  TeamAreaIndicatorsApiFactory,
  TeamAttritionRiskApiFactory,
  TeamDevelopmentActivitiesApiFactory,
  TeamDevelopmentEmployeesApiFactory,
  TeamDevelopmentStatisticsApiFactory,
  TeamMatrixApiFactory,
  TeamPmcDistributionApiFactory,
  TeamSupervisorsApiFactory,
  TestActivityReportApiFactory,
  TestExecutionApiFactory,
  TestQuestionPoolApiFactory,
  TestResultsApiFactory,
  TestStageConfigApiFactory,
  TestStatisticsApiFactory,
  TestTermApiFactory,
  TestTermParticipantApiFactory,
  TestTermSecurityEventApiFactory,
  ValidationApiFactory,
} from '@/api/generated';
import { ChatDocumentsApiFactory } from '@/api/generated/apis/chat-documents-api';
import { ProfessionalActivitiesApiFactory } from '@/api/generated/apis/professional-activities-api';
import { ProfessionalActivityDefinitionsApiFactory } from '@/api/generated/apis/professional-activity-definitions-api';
import { RestUploadsApiFactory } from '@/api/generated/apis/rest-uploads-api';
import { CONFIG } from '@/config/config.ts';

import { useAxiosInstance } from './useAxiosInstance.util';

interface Return {
  adminImpersonationApi: ReturnType<typeof AdminImpersonationApiFactory>;
  applicationDataApi: ReturnType<typeof ApplicationDataUpdateApiFactory>;
  appStatsReaderRestApi: ReturnType<typeof AppStatsReaderRestApiFactory>;
  avatarReaderApi: ReturnType<typeof EmployeeProfileAvatarReaderRestApiFactory>;
  avatarUpdaterApi: ReturnType<typeof EmployeeProfileAvatarUpdaterRestApiFactory>;
  certificationManagementApi: ReturnType<typeof CertificationManagementApiFactory>;
  certificationPlanStageApi: ReturnType<typeof CertificationPlanStageApiFactory>;
  certificationStageApi: ReturnType<typeof CertificationStageApiFactory>;
  chatDocumentsApi: ReturnType<typeof ChatDocumentsApiFactory>;
  commissionCandidatesApi: ReturnType<typeof CommissionCandidatesApiFactory>;
  currentUserApi: ReturnType<typeof RestCurrentUserApiFactory>;
  developmentCenterParticipantApi: ReturnType<typeof DevelopmentCenterParticipantApiFactory>;
  developmentCenterStageApi: ReturnType<typeof DevelopmentCenterStageApiFactory>;
  developmentCycleCampaignsApi: ReturnType<typeof DevelopmentCycleCampaignsApiFactory>;
  devPlanEvalApi: ReturnType<typeof DevPlanEvalApiFactory>;
  dictionariesApi: ReturnType<typeof DictionariesApiFactory>;
  employeeCertificationApi: ReturnType<typeof EmployeeCertificationApiFactory>;
  employeeDevelopmentPathApi: ReturnType<typeof EmployeeDevelopmentPathApiFactory>;
  employeeProfileSearchApi: ReturnType<typeof EmployeeProfileSearchApiFactory>;
  employeesSearchApi: ReturnType<typeof EmployeeSearchApiFactory>;
  employeeTalentPanelApi: ReturnType<typeof EmployeeTalentPanelApiFactory>;
  examDayApi: ReturnType<typeof ExamDayApiFactory>;
  examDayParticipantApi: ReturnType<typeof ExamDayParticipantApiFactory>;
  examineeExamApi: ReturnType<typeof ExamineeExamApiFactory>;
  examineeExamPreviewApi: ReturnType<typeof ExamineeExamPreviewApiFactory>;
  examParticipantApi: ReturnType<typeof ExamParticipantApiFactory>;
  examStageApi: ReturnType<typeof ExamStageApiFactory>;
  extraCommissionMembersApi: ReturnType<typeof ExtraCommissionMembersApiFactory>;
  featureFlagsApi: ReturnType<typeof RestFeatureFlagsApiFactory>;
  filesTemplateRestApi: ReturnType<typeof FilesTemplatesRestApiFactory>;
  hierarchySimulationRestApi: ReturnType<typeof HierarchySimulationRestApiFactory>;
  hierarchyUnitsManagerRestApi: ReturnType<typeof HierarchyUnitsManagerRestApiFactory>;
  hierarchyUnitsReaderRestApi: ReturnType<typeof HierarchyUnitsReaderRestApiFactory>;
  hrStatisticsApi: ReturnType<typeof HrStatisticsApiFactory>;
  individualExamApi: ReturnType<typeof IndividualExamApiFactory>;
  legalDocumentsApi: ReturnType<typeof LegalDocumentsApiFactory>;
  legalDocumentsManagementApi: ReturnType<typeof LegalDocumentsManagementApiFactory>;
  newsApi: ReturnType<typeof NewsApiFactory>;
  newsManagementApi: ReturnType<typeof NewsManagementApiFactory>;
  practicalExamActivityReportApi: ReturnType<typeof PracticalExamActivityReportApiFactory>;
  practicalExamAttemptApi: ReturnType<typeof PracticalExamAttemptApiFactory>;
  practicalExamQuestionPoolApi: ReturnType<typeof PracticalExamQuestionPoolApiFactory>;
  practicalExamReportAttachmentPreviewApi: ReturnType<
    typeof PracticalExamReportAttachmentPreviewApiFactory
  >;
  practicalExamSecurityEventApi: ReturnType<typeof PracticalExamSecurityEventApiFactory>;
  practicalExamShareTokenApi: ReturnType<typeof PracticalExamShareTokenApiFactory>;
  practicalExamStatisticsApi: ReturnType<typeof PracticalExamStatisticsApiFactory>;
  professionalActivitiesApi: ReturnType<typeof ProfessionalActivitiesApiFactory>;
  professionalActivityDefinitionsApi: ReturnType<typeof ProfessionalActivityDefinitionsApiFactory>;
  restEmployeeProfileApi: ReturnType<typeof RestEmployeeProfileApiFactory>;
  restEmployeeSectionsReaderApi: ReturnType<typeof RestEmployeeSectionsReaderApiFactory>;
  restEmployeeSectionsUpdaterApi: ReturnType<typeof RestEmployeeSectionsUpdaterApiFactory>;
  restNotificationControllerApi: ReturnType<typeof RestNotificationControllerApiFactory>;
  restUploadsApi: ReturnType<typeof RestUploadsApiFactory>;
  stageParticipantApi: ReturnType<typeof StageParticipantApiFactory>;
  structureBrowserApi: ReturnType<typeof StructureBrowserApiFactory>;
  successionCareerPathRestApi: ReturnType<typeof SuccessionCareerPathRestApiFactory>;
  successionChangeLogRestApi: ReturnType<typeof SuccessionChangeLogRestApiFactory>;
  successionReportRestApi: ReturnType<typeof SuccessionReportRestApiFactory>;
  successionSearchRestApi: ReturnType<typeof SuccessionSearchRestApiFactory>;
  successorPositionStateRestApi: ReturnType<typeof SuccessorPositionStateRestApiFactory>;
  surveyApi: ReturnType<typeof SurveyApiFactory>;
  surveyManagementApi: ReturnType<typeof SurveyManagementApiFactory>;
  surveyParticipationApi: ReturnType<typeof SurveyParticipationApiFactory>;
  tasksApi: ReturnType<typeof TasksApiFactory>;
  teamAreaIndicatorsApi: ReturnType<typeof TeamAreaIndicatorsApiFactory>;
  teamAttritionRiskApi: ReturnType<typeof TeamAttritionRiskApiFactory>;
  teamDevelopmentActivitiesApi: ReturnType<typeof TeamDevelopmentActivitiesApiFactory>;
  teamDevelopmentEmployeesApi: ReturnType<typeof TeamDevelopmentEmployeesApiFactory>;
  teamDevelopmentStatisticsApi: ReturnType<typeof TeamDevelopmentStatisticsApiFactory>;
  teamMatrixApi: ReturnType<typeof TeamMatrixApiFactory>;
  teamPmcDistributionApi: ReturnType<typeof TeamPmcDistributionApiFactory>;
  teamSupervisorsApi: ReturnType<typeof TeamSupervisorsApiFactory>;
  testActivityReportApi: ReturnType<typeof TestActivityReportApiFactory>;
  testExecutionApi: ReturnType<typeof TestExecutionApiFactory>;
  testQuestionPoolApi: ReturnType<typeof TestQuestionPoolApiFactory>;
  testResultsApi: ReturnType<typeof TestResultsApiFactory>;
  testStageConfigApi: ReturnType<typeof TestStageConfigApiFactory>;
  testStatisticsApi: ReturnType<typeof TestStatisticsApiFactory>;
  testTermApi: ReturnType<typeof TestTermApiFactory>;
  testTermParticipantApi: ReturnType<typeof TestTermParticipantApiFactory>;
  testTermSecurityEventApi: ReturnType<typeof TestTermSecurityEventApiFactory>;
  userPermissionsApi: ReturnType<typeof RestUserPermissionsApiFactory>;
  userRolesApi: ReturnType<typeof RestUserRolesApiFactory>;
  usersManagementApi: ReturnType<typeof RestUsersManagementApiFactory>;
  validationApi: ReturnType<typeof ValidationApiFactory>;
}

export const useApiClient = (): Return => {
  const basePath = CONFIG.HOST;
  const configuration = useMemo(() => getConfiguration(basePath), [basePath]);
  const axiosInstance = useAxiosInstance();

  return useMemo(() => {
    return {
      adminImpersonationApi: AdminImpersonationApiFactory(configuration, undefined, axiosInstance),
      applicationDataApi: ApplicationDataUpdateApiFactory(configuration, undefined, axiosInstance),
      appStatsReaderRestApi: AppStatsReaderRestApiFactory(configuration, undefined, axiosInstance),
      avatarReaderApi: EmployeeProfileAvatarReaderRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      avatarUpdaterApi: EmployeeProfileAvatarUpdaterRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      certificationManagementApi: CertificationManagementApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      certificationPlanStageApi: CertificationPlanStageApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      certificationStageApi: CertificationStageApiFactory(configuration, undefined, axiosInstance),
      chatDocumentsApi: ChatDocumentsApiFactory(configuration, undefined, axiosInstance),
      commissionCandidatesApi: CommissionCandidatesApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      currentUserApi: RestCurrentUserApiFactory(configuration, undefined, axiosInstance),
      developmentCenterParticipantApi: DevelopmentCenterParticipantApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      developmentCenterStageApi: DevelopmentCenterStageApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      developmentCycleCampaignsApi: DevelopmentCycleCampaignsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      devPlanEvalApi: DevPlanEvalApiFactory(configuration, undefined, axiosInstance),
      dictionariesApi: DictionariesApiFactory(configuration, undefined, axiosInstance),
      employeeCertificationApi: EmployeeCertificationApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      employeeDevelopmentPathApi: EmployeeDevelopmentPathApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      employeeProfileSearchApi: EmployeeProfileSearchApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      employeesSearchApi: EmployeeSearchApiFactory(configuration, undefined, axiosInstance),
      employeeTalentPanelApi: EmployeeTalentPanelApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      examDayApi: ExamDayApiFactory(configuration, undefined, axiosInstance),
      examDayParticipantApi: ExamDayParticipantApiFactory(configuration, undefined, axiosInstance),
      examineeExamApi: ExamineeExamApiFactory(configuration, undefined, axiosInstance),
      examineeExamPreviewApi: ExamineeExamPreviewApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      examParticipantApi: ExamParticipantApiFactory(configuration, undefined, axiosInstance),
      examStageApi: ExamStageApiFactory(configuration, undefined, axiosInstance),
      extraCommissionMembersApi: ExtraCommissionMembersApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      featureFlagsApi: RestFeatureFlagsApiFactory(configuration, undefined, axiosInstance),
      filesTemplateRestApi: FilesTemplatesRestApiFactory(configuration, undefined, axiosInstance),
      hierarchySimulationRestApi: HierarchySimulationRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      hierarchyUnitsManagerRestApi: HierarchyUnitsManagerRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      hierarchyUnitsReaderRestApi: HierarchyUnitsReaderRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      hrStatisticsApi: HrStatisticsApiFactory(configuration, undefined, axiosInstance),
      individualExamApi: IndividualExamApiFactory(configuration, undefined, axiosInstance),
      legalDocumentsApi: LegalDocumentsApiFactory(configuration, undefined, axiosInstance),
      legalDocumentsManagementApi: LegalDocumentsManagementApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      newsApi: NewsApiFactory(configuration, undefined, axiosInstance),
      newsManagementApi: NewsManagementApiFactory(configuration, undefined, axiosInstance),
      practicalExamActivityReportApi: PracticalExamActivityReportApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      practicalExamAttemptApi: PracticalExamAttemptApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      practicalExamQuestionPoolApi: PracticalExamQuestionPoolApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      practicalExamReportAttachmentPreviewApi: PracticalExamReportAttachmentPreviewApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      practicalExamSecurityEventApi: PracticalExamSecurityEventApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      practicalExamShareTokenApi: PracticalExamShareTokenApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      practicalExamStatisticsApi: PracticalExamStatisticsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      professionalActivitiesApi: ProfessionalActivitiesApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      professionalActivityDefinitionsApi: ProfessionalActivityDefinitionsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      restEmployeeProfileApi: RestEmployeeProfileApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      restEmployeeSectionsReaderApi: RestEmployeeSectionsReaderApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      restEmployeeSectionsUpdaterApi: RestEmployeeSectionsUpdaterApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      restNotificationControllerApi: RestNotificationControllerApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      restUploadsApi: RestUploadsApiFactory(configuration, undefined, axiosInstance),
      stageParticipantApi: StageParticipantApiFactory(configuration, undefined, axiosInstance),
      structureBrowserApi: StructureBrowserApiFactory(configuration, undefined, axiosInstance),
      successionCareerPathRestApi: SuccessionCareerPathRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      successionChangeLogRestApi: SuccessionChangeLogRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      successionReportRestApi: SuccessionReportRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      successionSearchRestApi: SuccessionSearchRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      successorPositionStateRestApi: SuccessorPositionStateRestApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      surveyApi: SurveyApiFactory(configuration, undefined, axiosInstance),
      surveyManagementApi: SurveyManagementApiFactory(configuration, undefined, axiosInstance),
      surveyParticipationApi: SurveyParticipationApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      tasksApi: TasksApiFactory(configuration, undefined, axiosInstance),
      teamAreaIndicatorsApi: TeamAreaIndicatorsApiFactory(configuration, undefined, axiosInstance),
      teamAttritionRiskApi: TeamAttritionRiskApiFactory(configuration, undefined, axiosInstance),
      teamDevelopmentActivitiesApi: TeamDevelopmentActivitiesApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      teamDevelopmentEmployeesApi: TeamDevelopmentEmployeesApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      teamDevelopmentStatisticsApi: TeamDevelopmentStatisticsApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      teamMatrixApi: TeamMatrixApiFactory(configuration, undefined, axiosInstance),
      teamPmcDistributionApi: TeamPmcDistributionApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      teamSupervisorsApi: TeamSupervisorsApiFactory(configuration, undefined, axiosInstance),
      testActivityReportApi: TestActivityReportApiFactory(configuration, undefined, axiosInstance),
      testExecutionApi: TestExecutionApiFactory(configuration, undefined, axiosInstance),
      testQuestionPoolApi: TestQuestionPoolApiFactory(configuration, undefined, axiosInstance),
      testResultsApi: TestResultsApiFactory(configuration, undefined, axiosInstance),
      testStageConfigApi: TestStageConfigApiFactory(configuration, undefined, axiosInstance),
      testStatisticsApi: TestStatisticsApiFactory(configuration, undefined, axiosInstance),
      testTermApi: TestTermApiFactory(configuration, undefined, axiosInstance),
      testTermParticipantApi: TestTermParticipantApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      testTermSecurityEventApi: TestTermSecurityEventApiFactory(
        configuration,
        undefined,
        axiosInstance,
      ),
      userPermissionsApi: RestUserPermissionsApiFactory(configuration, undefined, axiosInstance),
      userRolesApi: RestUserRolesApiFactory(configuration, undefined, axiosInstance),
      usersManagementApi: RestUsersManagementApiFactory(configuration, undefined, axiosInstance),
      validationApi: ValidationApiFactory(configuration, undefined, axiosInstance),
    };
  }, [axiosInstance, configuration]);
};

const getConfiguration = (basePath: string): Configuration => ({
  basePath,
  isJsonMime(_mime: string): boolean {
    return true;
  },
});
